import { useRef, useCallback, useEffect } from 'react';

// ⚡️ PERFORMANCE OPTIMIZATION: Web Worker hook for CPU-heavy tasks
export const useWebWorker = () => {
  const workerRef = useRef(null);
  const pendingRequests = useRef(new Map());
  const ALLOWED_TYPES = useRef(new Set([
    'SANITIZE_TEXT',
    'ENCRYPT_TEXT',
    'DECRYPT_TEXT',
    'PROCESS_MESSAGES',
    'VALIDATE_FILE',
    'VALIDATE_MESSAGE',
    'VERIFY_INTEGRITY',
    'BATCH_PROCESS'
  ]));
  const MAX_WORKER_PAYLOAD_BYTES = 500 * 1024; // 500KB

  // Minimal production-only client error reporter
  const safeReportError = async (message, context = {}) => {
    try {
      if (process.env.NODE_ENV !== 'production') return;
      const payload = {
        message: String(message || 'unknown_error'),
        context: { location: typeof window !== 'undefined' ? (window.location?.pathname || '/') : '/', ...context },
        ts: Date.now(),
      };
      await fetch('/api/client-logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch (_) {}
  };

  // ⚡️ PERFORMANCE OPTIMIZATION: Initialize worker
  useEffect(() => {
    if (typeof Worker !== 'undefined') {
      try {
        const workerUrl = '/worker.js';
        // Enforce same-origin worker URL
        if (!(workerUrl.startsWith('/'))) {
          safeReportError('worker_url_not_same_origin');
          return;
        }
        workerRef.current = new Worker(workerUrl);
        
        // ⚡️ ERROR HANDLING: Add error listener for importScripts failures
        workerRef.current.addEventListener('error', (event) => {
          if (event.error && event.error.message && event.error.message.includes('importScripts')) {
            if (process.env.NODE_ENV === 'development') {
              // eslint-disable-next-line no-console
            } else {
              safeReportError('worker_import_scripts_failed');
            }
          }
        });
        
        // ⚡️ PERFORMANCE OPTIMIZATION: Handle worker messages
        workerRef.current.onmessage = (event) => {
          const { id, success, result, error } = event.data;
          
          if (pendingRequests.current.has(id)) {
            const { resolve, reject } = pendingRequests.current.get(id);
            pendingRequests.current.delete(id);
            
            if (success) {
              resolve(result);
            } else {
              reject(new Error(error));
            }
          }
        };
        
        // ⚡️ PERFORMANCE OPTIMIZATION: Handle worker errors
        workerRef.current.onerror = (error) => {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
          } else {
            safeReportError('worker_runtime_error');
          }
          // Clear all pending requests on error and fallback to main thread
          pendingRequests.current.forEach(({ resolve, reject }) => {
            try {
              const result = handleInMainThread('SANITIZE_TEXT', { text: '' });
              resolve(result);
            } catch (err) {
              reject(new Error('Worker error - fallback failed'));
            }
          });
          pendingRequests.current.clear();
        };
        
        // Web Worker initialized successfully
      } catch (error) {
        safeReportError('worker_initialize_failed');
      }
    }
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Cleanup worker on unmount
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
        pendingRequests.current.clear();
      }
    };
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Send message to worker
  const sendMessage = useCallback((type, data) => {
    return new Promise((resolve, reject) => {
      // Validate type allowlist
      if (!ALLOWED_TYPES.current.has(type)) {
        const fallback = handleInMainThread(type, data);
        resolve(fallback);
        return;
      }
      // Cap payload size to avoid blocking the worker channel
      try {
        const approxSize = JSON.stringify({ t: type, d: data }).length;
        if (approxSize > MAX_WORKER_PAYLOAD_BYTES) {
          safeReportError('worker_payload_too_large', { type });
          const fallback = handleInMainThread(type, data);
          resolve(fallback);
          return;
        }
      } catch (_) {}

      if (!workerRef.current) {
        // Fallback to main thread if worker is not available
        resolve(handleInMainThread(type, data));
        return;
      }
      
      const id = Date.now() + Math.random();
      pendingRequests.current.set(id, { resolve, reject });
      
      workerRef.current.postMessage({
        type,
        data,
        id
      });
      
      // ⚡️ PERFORMANCE OPTIMIZATION: Timeout for worker requests
      setTimeout(() => {
        if (pendingRequests.current.has(id)) {
          pendingRequests.current.delete(id);
          reject(new Error('Worker request timeout'));
        }
      }, 10000); // 10 second timeout
    });
  }, []);

  // 🚀 ENHANCED SECURITY FUNCTIONS: Centralized security operations
  const sanitizeText = useCallback(async (text) => {
    try {
      const result = await sendMessage('SANITIZE_TEXT', { text });
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
      } else {
        safeReportError('worker_sanitize_failed');
      }
      return handleInMainThread('SANITIZE_TEXT', { text });
    }
  }, [sendMessage]);

  const validateFile = useCallback(async (file) => {
    try {
      const result = await sendMessage('VALIDATE_FILE', { file });
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
      } else {
        safeReportError('worker_validate_file_failed');
      }
      return handleInMainThread('VALIDATE_FILE', { file });
    }
  }, [sendMessage]);

  const validateMessage = useCallback(async (message) => {
    try {
      const result = await sendMessage('VALIDATE_MESSAGE', { message });
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
      } else {
        safeReportError('worker_validate_message_failed');
      }
      return handleInMainThread('VALIDATE_MESSAGE', { message });
    }
  }, [sendMessage]);

  const verifyMessageIntegrity = useCallback(async (message) => {
    try {
      const result = await sendMessage('VERIFY_INTEGRITY', { message });
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
      } else {
        safeReportError('worker_verify_integrity_failed');
      }
      return handleInMainThread('VERIFY_INTEGRITY', { message });
    }
  }, [sendMessage]);

  const processMessages = useCallback(async (messages, options = {}) => {
    try {
      const result = await sendMessage('PROCESS_MESSAGES', { messages, options });
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
      } else {
        safeReportError('worker_process_messages_failed');
      }
      return handleInMainThread('PROCESS_MESSAGES', { messages, options });
    }
  }, [sendMessage]);

  const batchProcessMessages = useCallback(async (messages, options = {}) => {
    try {
      const result = await sendMessage('BATCH_PROCESS', { messages, options });
      return result;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
      } else {
        safeReportError('worker_batch_process_failed');
      }
      return handleInMainThread('BATCH_PROCESS', { messages, options });
    }
  }, [sendMessage]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Fallback functions for main thread
  const handleInMainThread = (type, data) => {
    switch (type) {
      case 'SANITIZE_TEXT':
        return sanitizeTextInMainThread(data.text);
      case 'ENCRYPT_TEXT':
        return encryptTextInMainThread(data.text, data.key);
      case 'DECRYPT_TEXT':
        return decryptTextInMainThread(data.text, data.key);
      case 'PROCESS_MESSAGES':
        return processMessagesInMainThread(data.messages, data.options);
      case 'VALIDATE_FILE':
        return validateFileInMainThread(data.file);
      case 'VALIDATE_MESSAGE':
        return validateMessageInMainThread(data.message);
      case 'VERIFY_INTEGRITY':
        return verifyMessageIntegrityInMainThread(data.message);
      case 'BATCH_PROCESS':
        return batchProcessMessagesInMainThread(data.messages, data.options);
      default:
        return { error: 'Unknown message type' };
    }
  };

  // ⚡️ PERFORMANCE OPTIMIZATION: Main thread fallback functions
  const sanitizeTextInMainThread = (text) => {
    if (!text || typeof text !== 'string') return { sanitized: '', integrityHash: '' };
    
    // Basic sanitization without external libraries
    let sanitized = text.replace(/[\x00-\x1F\x7F]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    sanitized = sanitized.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    
    // Simple hash for integrity
    let hash = 0;
    if (sanitized.length > 0) {
      for (let i = 0; i < sanitized.length; i++) {
        const char = sanitized.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
    }
    
    return { sanitized, integrityHash: hash.toString() };
  };

  const validateFileInMainThread = (file) => {
    if (!file) return { isValid: false, error: 'No file provided' };
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', 'video/ogg'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    const dangerousExtensions = ['.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jar'];
    
    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: 'Invalid file type' };
    }
    
    if (file.size > maxSize) {
      return { isValid: false, error: 'File too large' };
    }
    
    const fileName = file.name.toLowerCase();
    if (dangerousExtensions.some(ext => fileName.includes(ext))) {
      return { isValid: false, error: 'Dangerous file type' };
    }
    
    if (file.size === 0) {
      return { isValid: false, error: 'Empty file' };
    }
    
    return { isValid: true, error: null };
  };

  const validateMessageInMainThread = (message) => {
    if (!message || typeof message !== 'object') {
      return { isValid: false, error: 'Invalid message format' };
    }
    
    if (!message.text && !message.type) {
      return { isValid: false, error: 'Message must have text or type' };
    }
    
    if (message.text && message.text.length > 1000) {
      return { isValid: false, error: 'Message too long' };
    }
    
    if (!message.timestamp) {
      return { isValid: false, error: 'Message must have timestamp' };
    }
    
    return { isValid: true, error: null };
  };

  const verifyMessageIntegrityInMainThread = (message) => {
    if (!message || !message.text) return true;
    
    if (message.integrityHash) {
      let hash = 0;
      if (message.text.length > 0) {
        for (let i = 0; i < message.text.length; i++) {
          const char = message.text.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash;
        }
      }
      return hash.toString() === message.integrityHash;
    }
    
    return true;
  };

  const processMessagesInMainThread = (messages, options = {}) => {
    const { sanitize = true, validate = true, verifyIntegrity = true } = options;
    
    return messages.map(message => {
      const processed = { ...message };
      
      if (validate) {
        const validation = validateMessageInMainThread(message);
        if (!validation.isValid) {
          processed.error = validation.error;
          return processed;
        }
      }
      
      if (sanitize && message.text) {
        const sanitization = sanitizeTextInMainThread(message.text);
        processed.text = sanitization.sanitized;
        processed.integrityHash = sanitization.integrityHash;
      }
      
      if (verifyIntegrity && !verifyMessageIntegrityInMainThread(processed)) {
        processed.error = 'Message integrity check failed';
      }
      
      return processed;
    });
  };

  const batchProcessMessagesInMainThread = (messages, options) => {
    return processMessagesInMainThread(messages, options);
  };

  const encryptTextInMainThread = (text, key) => {
    if (!text || !key) return '';
    try {
      return btoa(encodeURIComponent(text));
    } catch (error) {
      return '';
    }
  };

  const decryptTextInMainThread = (encryptedText, key) => {
    if (!encryptedText || !key) return '';
    try {
      return decodeURIComponent(atob(encryptedText));
    } catch (error) {
      return '';
    }
  };

  // 🚀 NEW: Check if worker is available
  const isWorkerAvailable = useCallback(() => {
    return workerRef.current !== null;
  }, []);

  return {
    // 🚀 ENHANCED SECURITY FUNCTIONS
    sanitizeText,
    validateFile,
    validateMessage,
    verifyMessageIntegrity,
    processMessages,
    batchProcessMessages,
    
    // ⚡️ LEGACY FUNCTIONS (for backward compatibility)
    encryptText: useCallback(async (text, key) => {
      try {
        return await sendMessage('ENCRYPT_TEXT', { text, key });
      } catch (error) {
        return encryptTextInMainThread(text, key);
      }
    }, [sendMessage]),
    
    decryptText: useCallback(async (text, key) => {
      try {
        return await sendMessage('DECRYPT_TEXT', { text, key });
      } catch (error) {
        return decryptTextInMainThread(text, key);
      }
    }, [sendMessage]),
    
    // 🚀 UTILITY FUNCTIONS
    isWorkerAvailable
  };
}; 